<template>
  <BaseModule
    v-bind="$props"
    class="experiences-module"
    data-module="experiences"
  >
    <Warning
      v-if="editing"
      text="O módulo não possui um hotel selecionado"
    >
    </Warning>

    <Loader v-if="loading" />

    <EmptyState
      v-if="isEmptyState"
      :title="$t('notAvailableTitle')"
      :subtitle="$t('notAvailableSubtitle')"
      :img="experiencesImg"
    />
    <div v-if="showExperiences">
      <header
        v-if="title || subtitle"
        class="base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="title"
          class="tit tit-g"
        >
          {{ title }}
        </component>
        <h3
          v-if="subtitle"
          class="sub"
        >
          {{ subtitle }}
        </h3>
      </header>

      <!-- Slider experiences container -->
      <div
        ref="swiper"
        class="slider-experiencias swiper"
      >
        <ul class="swiper-wrapper">
          <li
            v-for="exp in experiencesList"
            :key="exp.Codigo"
            :data-experience="exp.Codigo"
            class="bloco-exp swiper-slide"
          >
            <span
              v-if="exp.PercentualDesconto"
              class="promo"
            >
              {{ $tc('discount', Math.trunc(exp.PercentualDesconto)) }}
            </span>
            <div class="exp-foto">
              <LImage
                v-if="exp.UrlImagem"
                :src="exp.UrlImagem"
                :alt="exp.Nome"
              />
            </div>

            <div class="bloco-exp__content">
              <h3 class="bloco-exp__nome">
                {{ exp.Nome }}
              </h3>

              <div
                class="bloco-exp__info"
                :class="{ 'bloco-exp__info--ler-mais': exp.verMais }"
              >
                <div
                  v-if="exp.MenorPreco"
                  class="valor"
                >
                  <div
                    v-if="exp.PercentualDesconto"
                    class="valor__label small-info"
                  >
                    <span
                      class="valor valor-promo"
                      v-html="valueFormat(exp.MenorPrecoSemDesconto)"
                    >
                    </span>
                  </div>
                  <div
                    v-else
                    class="small-info"
                  >
                    {{ $t('priceText') }}
                  </div>
                  <div
                    class="half-content valor__info"
                    :style="{ color: getPrecoColor(exp) }"
                  >
                    <span v-html="getValueHTML(exp.MenorPreco)"></span>
                    <span class="small-info"> /{{ unitType(exp) }}</span>
                  </div>
                  <div
                    class="half-content valor__disclaimer"
                    @mouseover="showTooltip"
                    @mouseleave="hideTooltip"
                  >
                    <span
                      v-if="exp.PoliticaResumida"
                      class="slb-tooltip arrow-right-down"
                    >
                      {{ exp.PoliticaResumida }}
                    </span>
                    <span
                      v-if="exp.PoliticaResumida"
                      class="info-icon"
                    ></span>
                  </div>
                </div>
                <div
                  v-else
                  class="valor--empty"
                >
                  <!-- filler div to centralize column in case the item has no price registered -->
                </div>

                <div
                  class="description"
                  :class="{ 'description--empty': !hasText(exp.Descricao) }"
                >
                  <p
                    :ref="'descriptio-text-' + exp.Codigo"
                    class="description__text"
                  >
                    {{ exp.Descricao }}
                  </p>
                </div>

                <button
                  v-if="hasText(exp.Descricao)"
                  class="toggle-ver-mais"
                  :class="{ 'toggle-ver-mais--hidden': !exp.seeMoreEnabled }"
                  type="button"
                  @click="toggleVerMais(exp)"
                >
                  <span
                    v-if="!exp.verMais"
                    class="more"
                    >{{ $t('readMore') }}</span
                  >
                  <span
                    v-else
                    class="less"
                    >{{ $t('readLess') }}</span
                  >
                </button>
              </div>

              <EditorLink
                :model="getLinkLetsBook(exp.LinkPublico)"
                class="cta"
              >
                {{ $t('book') }}
              </EditorLink>
            </div>
          </li>
        </ul>

        <!-- If we need navigation buttons -->
        <div class="buttons">
          <div class="custom-button-prev-main">
            <IconArrow
              direction="left"
              size="md"
            />
          </div>
          <div class="custom-button-next-main">
            <IconArrow
              direction="right"
              size="md"
            />
          </div>
        </div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import { mapGetters } from 'vuex';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

import getApiHotels from '@/mixins/getApiHotels';
import base from '@/mixins/base';
import formatCurrency from '@/helpers/formatCurrency';
import Loader from '@/components/Loader.vue';
import Warning from '@/components/Warning.vue';
import IconArrow from '@/components/icons/IconArrow.vue';
import EditorLink from './EditorLink.vue';
import EmptyState from './EmptyState.vue';
import BaseModule from './generic/BaseModule.vue';

const emptyExperiencesUrlPath = require('@/assets/empty-accommodations.png');

export default {
  name: 'Experiences',
  components: {
    BaseModule,
    Loader,
    Warning,
    EditorLink,
    EmptyState,
    IconArrow,
  },
  mixins: [getApiHotels, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    hotel: {
      type: Number,
      default: null,
    },
    backgroundColor: {
      type: String,
      default: '#ffffff',
    },
  },
  data() {
    return {
      loading: false,
      empty: false,
      experiences: [],
      selectedExperience: null,
      experiencesImg: emptyExperiencesUrlPath,
    };
  },
  computed: {
    experiencesList() {
      return this.experiences;
    },
    hasExperiences() {
      return this.experiencesList.length > 0;
    },
    showExperiences() {
      return !this.loading && this.hasExperiences && !this.empty;
    },
    editing() {
      return typeof this.$parent === 'undefined' && !this.hotel;
    },
    isEmptyState() {
      return !this.hasExperiences && !this.loading && !this.editing;
    },
    ...mapGetters(['settings']),
  },
  watch: {
    hotel(val) {
      if (typeof val === 'number') {
        this.requestExperiences();
      }
    },
    experiences(val) {
      if (val && val.length > 0 && !this.loading) {
        this.$nextTick(() => {
          // eslint-disable-next-line no-new
          new Swiper(this.$refs.swiper, {
            modules: [Navigation],
            slidesPerView: 'auto',
            slidesPerGroup: 1,
            spaceBetween: 20,
            followFinger: false,
            allowTouchMove: false,
            breakpoints: {
              320: {
                slidesPerView: 1,
                slidesPerGroup: 1,
              },
              640: {
                slidesPerView: 2,
                slidesPerGroup: 1,
              },
              1024: {
                slidesPerView: 3,
                slidesPerGroup: 3,
              },
            },
            navigation: {
              nextEl: '.custom-button-next-main',
              prevEl: '.custom-button-prev-main',
            },
          });

          this.manageSeeMore();
        });
      }
    },
  },
  mounted() {
    this.getApiHotels();

    if (this.hotel) {
      this.requestExperiences();
    }
  },
  methods: {
    manageSeeMore() {
      this.experiencesList.forEach((exp) => {
        const el = this.$refs[`descriptio-text-${exp.Codigo}`];
        if (!el || el[0].offsetHeight <= 24 * 3) {
          // eslint-disable-next-line no-param-reassign
          exp.seeMoreEnabled = false;
        }
      });
    },
    async requestExperiences() {
      this.loading = true;
      const urlExperiences = `${this.settings.letsbookApiId}/experiencia/${this.hotel}/${this.apiLocale}`;

      if (!this.apiData[urlExperiences]) {
        try {
          await this.$store.dispatch('loadApiData', urlExperiences);
        } catch {
          return;
        }
      }

      this.experiences = this.apiData[urlExperiences].map((exp) => ({
        ...exp,
        verMais: false,
        seeMoreEnabled: true,
      }));
      this.loading = false;
    },
    hasText(text) {
      return typeof text === 'string' && /\S/.test(text);
    },
    getPrecoColor(exp) {
      return exp.Destino.MenorTarifa.TemDesconto
        ? this.settings.promotionColor
        : this.settings.primaryColor;
    },
    toggleVerMais(exp) {
      // eslint-disable-next-line no-param-reassign
      exp.verMais = !exp.verMais;
    },
    getLinkLetsBook(linkHash) {
      return this.settings.letsbookUrl.replace(
        /\/admin\/?$/i,
        `/experiencias/linkpublico?r=${linkHash}`,
      );
    },
    valueFormat(price) {
      return formatCurrency(price, 'BRL');
    },
    getValueHTML(value) {
      const formattedValue = this.valueFormat(value);
      const rgx = /(\D*)(\d{1,3}(?:[.,\s]\d{3})*)((?:[.,\s]\d{2})?\D*)/; // (any)(INT)(any)
      return formattedValue.replace(rgx, '$1<span class="integer">$2</span>$3'); // $1 might be empty
    },
    unitType(exp) {
      if (exp.CategoriaNome === 'Avulso' || exp.Categoria === '4') {
        return this.$t('unit');
      }
      return this.$t('person');
    },
    showTooltip(event) {
      const el = event.currentTarget;
      const slbTooltip = el.querySelector('.slb-tooltip');
      const position = el.getBoundingClientRect();
      if (position.top < slbTooltip.clientHeight + 10) {
        slbTooltip.classList.add('open-down');
      } else {
        slbTooltip.classList.add('open-up');
      }
    },
    hideTooltip(event) {
      const el = event.currentTarget;
      const slbTooltip = el.querySelector('.slb-tooltip');
      slbTooltip.classList.remove('open-up', 'open-down');
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'hotel',
            position: 0,
            label: 'Hotel',
            fields: [
              {
                name: 'hotel',
                type: 'VueSelect',
                label: 'Listar experiências',
                helpText: 'Selecione um hotel para listar as experiências',
                options() {
                  const hotelsToOptions = (hotels) =>
                    hotels.map((hotel) => ({
                      label: hotel.Nome,
                      value: hotel.Codigo,
                    }));

                  // Are hotels already loaded?
                  if (this.hotels.length > 0) {
                    return hotelsToOptions(this.hotels);
                  }

                  // THIS IS PURE MAGIC
                  return new Promise((resolve) => {
                    const unwatch = this.$watch('hotels', (hotels) => {
                      resolve(hotelsToOptions(hotels));
                      unwatch();
                    });
                  });
                },
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            toRemove: ['textColor'],
          },
          {
            name: 'fonts',
            toRemove: ['contentSizeDesk', 'contentSizeMobile'],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        discount: '{count}% OFF',
        priceText: 'a partir de',
        unit: 'unidade',
        person: 'pessoa',
        readMore: 'Ler mais',
        readLess: 'Ler menos',
        book: 'Saiba Mais',
        notAvailableTitle:
          'Ops, não encontramos nenhuma experiência disponível.',
        notAvailableSubtitle:
          'Em breve listaremos aqui todas as nossas experiências para você escolher a que mais se encaixa no seu perfil.',
      },
      en: {
        discount: '{count}% OFF',
        priceText: 'prices from',
        unit: 'unit',
        person: 'person',
        readMore: 'Read more',
        readLess: 'Less',
        book: 'Know More',
        notAvailableTitle: 'Ops, we did not find any available item.',
        notAvailableSubtitle:
          "We'll soon list all our experiencies here so that you can find the one that best suits your profile.",
      },
      es: {
        discount: '{count}% OFF',
        priceText: 'a partir de',
        unit: 'unidad',
        person: 'persona',
        readMore: 'Lea más',
        readLess: 'Menos',
        book: 'Saiba Más',
        notAvailableTitle: 'Ops, no encontramos experiencias disponibles.',
        notAvailableSubtitle:
          'En breve enumeraremos aquí todos nuestros experiencias para que elijas el que mejor se adapta a tu perfil.',
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.experiences-module {
  text-align: center;
  overflow: hidden;
  padding: 0 20px;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  @include media-breakpoint-down(sm) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }

  &__empty {
    text-align: center;
    padding: 20px 20px;
  }

  .scroll-area {
    height: auto;
    margin: 0;
    max-height: calc(100vh - 65px);
  }

  .slider-experiencias {
    overflow: initial;
    padding-left: 0;
    padding-right: 0;
    background-color: transparent;
    padding: 0 50px;
    position: relative;
    width: 100%;
    max-width: 980px;

    &::before,
    &::after {
      background-color: var(--bg-color);
      opacity: 0.85;
      content: '';
      display: block;
      position: absolute;
      top: 0;
      width: calc(50vw - 50% + 40px);
      height: 100%;
      z-index: 2;
    }

    &::before {
      right: calc(100% - 40px);
    }

    &::after {
      left: calc(100% - 40px);
    }

    & > .swiper-wrapper {
      &--centered {
        display: flex;
        justify-content: center;
      }
    }

    .custom-button-prev-main,
    .custom-button-next-main {
      position: absolute;
      padding: 4px;
      top: 50%;
      z-index: 999;
      border-radius: 50%;
      background-color: rgba($lb-black, 0.333);
      color: $lb-white;
      cursor: pointer;

      &.swiper-button-disabled {
        display: none;
      }

      .arrow {
        width: 15px;
      }
    }

    .custom-button-prev-main {
      left: 0;
      margin-left: -40px;

      @include media-breakpoint-down(lg) {
        margin-left: -20px;
      }

      @include media-breakpoint-down(sm) {
        margin-left: -10px;
      }
    }

    .custom-button-next-main {
      right: 0;
      margin-right: -40px;

      @include media-breakpoint-down(lg) {
        margin-right: -20px;
      }

      @include media-breakpoint-down(sm) {
        margin-right: -10px;
      }
    }
  }

  .bloco-exp {
    overflow: hidden;
    border-radius: 4px;
    background-color: $lb-white;
    position: relative;
    transition: opacity 0.2s;
    width: 33.333%;
    z-index: 0;

    &:hover {
      z-index: 99;
    }

    .promo {
      font-size: $size-normal;
      font-weight: $lb-bold;
      line-height: 2.6em;
      padding: 0 15px;
      text-transform: uppercase;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 50;
      color: $lb-white;
      background-color: var(--promo-color);
    }

    &__content {
      padding: 0 16px 16px;
    }

    &__nome {
      color: $lb-dark-gray;
      display: block;
      text-align: left;
      font-size: $size-small;
      font-weight: $lb-bold;
      height: 35px;
      line-height: 1.2em;
      margin: auto 0 15px;
      text-transform: uppercase;
    }

    &__info {
      width: 100%;
      margin-bottom: 25px;
      min-height: 190px;
      max-height: 190px;
      text-align: left;
      transition: max-height 0.3s ease-in-out;
      font-size: $size-small;

      .small-info {
        font-size: $size-smaller;
        color: $lb-medium-gray;
      }

      .half-content {
        display: inline-block;
        width: 50%;
        margin-right: -5px;
      }

      .valor {
        padding: 4px 0;

        &--empty {
          padding: 4px 0;
          min-height: 72px;
        }

        &__label {
          text-align: left;

          .valor-promo {
            color: $lb-light-gray;
            margin-bottom: 0.2em;
            text-decoration: line-through;
            text-transform: uppercase;
          }
        }

        &__info {
          font-weight: $lb-bold;
          text-transform: none;
          width: 85%;
          text-align: left;
          font-size: $size-smaller;

          ::v-deep .integer {
            font-size: 36px;
          }
        }

        &__disclaimer {
          text-align: right;
          width: 15%;
          position: relative;

          .slb-tooltip {
            right: -10px;
            visibility: hidden;
            opacity: 0;
            color: $lb-light-gray;
            left: unset;
            right: -10px;

            &.open-up,
            &.open-down {
              opacity: 1;
              visibility: visible;
              transform: translateY(0);
            }
          }
        }
      }

      .description {
        margin-top: 10px;
        text-align: left;
        overflow: hidden;
        max-height: calc(3 * 24px);
        min-height: calc(3 * 24px);
        transition: max-height 0.3s ease-in-out;
        border-top: 1px solid $lb-light-gray;
        padding: 4px 0;

        &--empty {
          height: calc(3 * 24px);
        }

        &__text {
          line-height: 24px;
          font-size: $size-small;
        }
      }

      .toggle-ver-mais {
        position: relative;
        border: 0;
        background: none;
        font-weight: $lb-bold;
        outline: inherit;
        padding: 5px 0;
        cursor: pointer;
        color: var(--primary-color);

        &--hidden {
          visibility: hidden;
          opacity: 0;
        }

        &::after {
          content: '';
          margin-left: 0.625vw;
          display: inline-block;
          border-top: 7px solid;
          border-left: 5px solid transparent !important;
          border-right: 5px solid transparent !important;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          transition: transform 0.4s ease-in-out;
        }
      }

      &--ler-mais {
        max-height: 1000px;

        .description {
          max-height: 1000px;
        }

        .toggle-ver-mais {
          &::after {
            transform: translateY(-50%) rotate(180deg);
          }
        }
      }
    }
  }

  .exp-foto {
    height: 220px;
    margin-bottom: 30px;
  }
}
</style>
